import { AddressFields } from 'components/form-fields/address-section/types'
import { INITIAL_FLOW } from 'features/registration/constants'
import { InvoiceMedium, PaymentMethod, RegistrationSteps, Situation } from 'features/registration/types'
import { getNextRegistrationStep, getPreviousRegistrationStep, getStartDateRange } from 'features/registration/utils'
import { Language } from 'types/language'
import { REGISTRATION_ERROR, REGISTRATION_START, REGISTRATION_SUCCESS } from './actions/register'
import { RESET_STORE } from './actions/resetRegistrationStore'
import { GO_TO_REGISTRATION_STEP, NEXT_REGISTRATION_STEP, PREVIOUS_REGISTRATION_STEP } from './actions/switchRegistrationStep'
import { RegistrationActionTypes } from './actions/types'
import { UPDATE_ALL_REGISTRATION_INPUTS } from './actions/updateAllRegistrationInputs'
import { UPDATE_LOADING } from './actions/updateLoading'
import { UPDATE_REGISTRATION_INPUT } from './actions/updateRegistrationInputs'
import {
  BillingFieldKeys,
  BirthDateFields,
  InputGroupKeys,
  MeterDetailsFieldKeys,
  OverviewFieldKeys,
  PersonalDataFieldKeys,
  RegistrationStore
} from './types'

const initialState: RegistrationStore = {
  isLoading: false,
  error: false,
  currentStep: RegistrationSteps.PERSONAL_DATA,
  registrationFlow: INITIAL_FLOW,
  inputs: {
    [InputGroupKeys.PERSONAL_DATA]: {
      [PersonalDataFieldKeys.IS_COMPANY]: false,
      [PersonalDataFieldKeys.FIRSTNAME]: '',
      [PersonalDataFieldKeys.LASTNAME]: '',
      [PersonalDataFieldKeys.EMAIL]: '',
      [PersonalDataFieldKeys.REPEAT_EMAIL]: '',
      [PersonalDataFieldKeys.MOBILE_PHONE]: '',
      [PersonalDataFieldKeys.DATE_OF_BIRTH]: {
        [BirthDateFields.DAY]: '',
        [BirthDateFields.MONTH]: '',
        [BirthDateFields.YEAR]: ''
      },
      [PersonalDataFieldKeys.LANGUAGE]: Language.DUTCH,
      [PersonalDataFieldKeys.DELIVERY_ADDRESS]: {
        [AddressFields.STREET]: '',
        [AddressFields.NUMBER]: '',
        [AddressFields.BOX]: '',
        [AddressFields.POSTAL_CODE]: '',
        [AddressFields.TOWN_NAME]: '',
        [AddressFields.TOWN_CODE]: 0
      }
    },
    [InputGroupKeys.METER_DETAILS]: {
      [MeterDetailsFieldKeys.SITUATION]: Situation.SWITCH,
      [MeterDetailsFieldKeys.METER_READING_MONTH]: null,
      [MeterDetailsFieldKeys.CONTRACT_START_DATE]: getStartDateRange(Situation.SWITCH).initial,
      [MeterDetailsFieldKeys.KNOWS_METER_DETAILS]: true,
      [MeterDetailsFieldKeys.ELECTRICITY]: {
        total: null,
        day: null,
        night: null,
        exclNight: null,
        ean: null,
        eanExclNight: null,
        meterNumber: null
      },
      [MeterDetailsFieldKeys.GAS]: {
        total: null,
        ean: null,
        meterNumber: null
      }
    },
    [InputGroupKeys.BILLING]: {
      [BillingFieldKeys.DIFFERENT_CORRESPONDENCE_ADDRESS]: false,
      [BillingFieldKeys.CORRESPONDENCE_ADDRESS]: {
        [AddressFields.STREET]: '',
        [AddressFields.NUMBER]: '',
        [AddressFields.BOX]: '',
        [AddressFields.POSTAL_CODE]: '',
        [AddressFields.TOWN_NAME]: '',
        [AddressFields.TOWN_CODE]: 0
      },
      [BillingFieldKeys.PAYMENT_METHOD]: PaymentMethod.DIRECT_DEBIT,
      [BillingFieldKeys.INVOICE_MEDIUM]: InvoiceMedium.DIGITAL,
      [BillingFieldKeys.IBAN]: ''
    },
    [InputGroupKeys.OVERVIEW]: {
      [OverviewFieldKeys.SIGNATURE]: ''
    }
  }
}

export const reducer = (state = initialState, action: RegistrationActionTypes): RegistrationStore => {
  switch (action.type) {
    case GO_TO_REGISTRATION_STEP: {
      const { step } = action

      return {
        ...state,
        currentStep: step
      }
    }

    case NEXT_REGISTRATION_STEP: {
      const { currentStep, registrationFlow } = state
      const nextStep = getNextRegistrationStep(registrationFlow, currentStep)

      return {
        ...state,
        currentStep: nextStep
      }
    }

    case PREVIOUS_REGISTRATION_STEP: {
      const { currentStep, registrationFlow } = state
      const previousStep = getPreviousRegistrationStep(registrationFlow, currentStep)

      return {
        ...state,
        currentStep: previousStep
      }
    }

    case REGISTRATION_START: {
      return {
        ...state,
        isLoading: true,
        error: false
      }
    }

    case REGISTRATION_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        currentStep: RegistrationSteps.CONFIRMATION
      }
    }

    case REGISTRATION_ERROR: {
      return {
        ...state,
        isLoading: false,
        error: true
      }
    }

    case RESET_STORE: {
      return initialState
    }

    case UPDATE_ALL_REGISTRATION_INPUTS: {
      const { inputs } = action

      return {
        ...state,
        inputs
      }
    }

    case UPDATE_LOADING: {
      const { loading } = action

      return {
        ...state,
        isLoading: loading
      }
    }

    case UPDATE_REGISTRATION_INPUT: {
      const { group, key, value } = action
      const inputs = { ...state.inputs }

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      inputs[group as InputGroupKeys][key] = value

      return {
        ...state,
        inputs
      }
    }

    default:
      return state
  }
}
